<template>
  <b-modal
      :id="props.id"
      :title="t('REGISTER_NEW_USER')"
      size="lg"
      role="dialog"
      ref="modalRef"
      @ok="addAccount"
      :ok-title="t('SAVE')"
      :cancel-title="t('CANCEL')"
  >
    <div>
      <b-form>
        <b-form-group
            label-for="add-account-ssn"
            :label="t('SSN')"
            :state="ssnState"
            :invalid-feedback="ssnInvalidFeedback"
        >
          <b-input type="text" v-model="formData.ssn" class="form-control" required/>
        </b-form-group>

        <b-form-group
            label-for="add-account-role"
            :label="t('ROLE')"
            :state="roleState"
            :invalid-feedback="roleInvalidFeedback"
        >
          <b-select id="add-account-role" v-model="formData.roleType" class="no-shadow" required>
            <option :value="role" v-for="role in roleOptions">{{ t(role) }}</option>
          </b-select>
        </b-form-group>
      </b-form>
    </div>
  </b-modal>
</template>

<script setup lang="ts">
import {computed, inject, ref} from "vue";
import {useI18n} from "vue-i18n";
import {Role} from "@/auth/oauth";
import {ErrorResponse, getViolationMessage} from "@/error/errorResponse";
import {validateSsn} from "@/util/estId";

interface AddAccountFormData {
  ssn?: string;
  roleType?: string;
}

const axios = inject("axios");
const {t} = useI18n();

const formData = ref<AddAccountFormData>({})
const errorResp = ref<ErrorResponse<AddAccountFormData>>({});
const submitted = ref(false);
const modalRef = ref(null);

const props = defineProps<{
  id: string;
  afterSave?: () => Promise<any>;
}>();

const roleState = computed(() => {
  return !submitted.value || !roleInvalidFeedback.value;
});

const ssnState = computed(() => {
  return !submitted.value || !ssnInvalidFeedback.value;
});

const roleInvalidFeedback = computed(() => {
  const violationMsg = getViolationMessage("roleType", errorResp.value);
  if (violationMsg) {
    return t(violationMsg);
  }

  if (!formData.value.roleType) {
    return t("ERRORS.FIELD_IS_REQUIRED");
  }

  return "";
});

const ssnInvalidFeedback = computed(() => {
  const violationMsg = getViolationMessage("ssn", errorResp.value);
  if (violationMsg) {
    return t(violationMsg);
  }

  if (!formData.value.ssn) {
    return t("ERRORS.FIELD_IS_REQUIRED");
  }

  if (!validateSsn(formData.value.ssn)) {
    return t("FAULTY_SSN");
  }

  return "";
});

const roleOptions = computed(() => {
  return Object.keys(Role).map(role => Role[role]).filter(role => role != Role.ALL && role != Role.DISABLED_USER);
});

const openModal = () => {
  submitted.value = false;
  formData.value = {};
  modalRef.value.show(props.id);
};

const addAccount = async (ev: Event) => {
  ev.preventDefault();
  submitted.value = true;

  if (!formData.value.ssn || !formData.value.roleType || !validateSsn(formData.value.ssn)) {
    return;
  }

  const requestData = {
    ssn: formData.value.ssn,
    roleType: formData.value.roleType,
  };

  try {
    await axios.post(`/account/by-admin`, requestData);
  } catch (e) {
    errorResp.value = e.response?.data;
    throw e;
  }

  if (props.afterSave) {
    await props.afterSave();
  }

  modalRef.value.hide(props.id);
};

defineExpose({
  openModal,
});
</script>

<style scoped lang="scss">

</style>
